import { TalkReportClientPacket } from 'eolib';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { Button, Input } from '@/ui/components';
import { useClient } from '@/ui/context';
import { UI_ITEM_BG, UI_PANEL_BORDER } from '@/ui/consts';
import { MAIN_WORLD } from '@/world';
import { isStaffSpell } from './staff-spell-panel';
import { DialogBase } from './dialog-base';

export function BotDialog() {
  const client = useClient();
  const [minutes, setMinutes] = useState('15');
  const [mode, setMode] = useState('melee');
  const [spell, setSpell] = useState('0');
  const [potions, setPotions] = useState(true);
  const [loot, setLoot] = useState(false);
  const [hp, setHp] = useState('50');
  const [tp, setTp] = useState('25');
  const [status, setStatus] = useState('Checking server status…');
  const [pending, setPending] = useState(false);
  const stage = useRef<'settings' | 'loot' | 'start' | null>(null);
  const settings = useRef({ minutes: '15', loot: false });
  const send = useCallback((command: string) => {
    if (MAIN_WORLD) return;
    const packet = new TalkReportClientPacket();
    packet.message = command;
    client.bus!.send(packet);
  }, [client]);

  useEffect(() => {
    if (MAIN_WORLD) return;
    const receive = ({ message }: { message: string }) => {
      if (!/bot/i.test(message)) return;
      setStatus(message);
      if (stage.current === 'settings' && message === 'Bot settings saved. Start the bot when ready.') {
        stage.current = 'loot';
        send(`#bot loot ${settings.current.loot ? 'on' : 'off'}`);
      } else if (stage.current === 'loot' && message.startsWith('Bot loot ')) {
        stage.current = 'start';
        send(`#bot start ${settings.current.minutes}`);
      } else {
        stage.current = null;
        setPending(false);
      }
    };
    client.chatController.subscribeServerChat(receive);
    send('#bot status');
    return () => client.chatController.unsubscribeServerChat(receive);
  }, [client, send]);

  useEffect(() => {
    if (!pending) return;
    const timeout = window.setTimeout(() => {
      stage.current = null;
      setPending(false);
      setStatus('No confirmation received. Check Status before trying again.');
    }, 10000);
    return () => window.clearTimeout(timeout);
  }, [pending]);

  if (MAIN_WORLD) return null;
  const start = () => {
    const duration = String(Math.max(1, Math.min(120, Number.parseInt(minutes, 10) || 15)));
    const threshold = (value: string) => Math.max(10, Math.min(90, Number.parseInt(value, 10) || 25));
    setMinutes(duration);
    settings.current = { minutes: duration, loot };
    stage.current = 'settings';
    setPending(true);
    send(`#bot configure ${mode} ${spell} ${potions ? 1 : 0} ${threshold(hp)} ${threshold(tp)}`);
  };
  return <DialogBase id='bot' title='Legacy Combat Bot' size='md'>
    <div class='flex flex-col gap-3 p-3 text-sm' data-legacy-bot-panel>
      <p class='m-0 opacity-80'>Hunt NPCs automatically. Manual movement or attacks stop the bot. It never attacks players.</p>
      <div class='grid grid-cols-3 gap-2' role='group' aria-label='Combat mode'>
        {['melee', 'cast', 'kite'].map(value => <Button disabled={pending} variant={mode === value ? 'primary' : 'secondary'} onClick={() => setMode(value)}>{value === 'cast' ? 'Casting' : value === 'kite' ? 'Kiting' : 'Melee'}</Button>)}
      </div>
      {mode !== 'melee' && <label>Attack spell
        <select class='select w-full mt-1' value={spell} onChange={e => setSpell(e.currentTarget.value)} disabled={pending}>
          <option value='0'>Auto — first learned attack spell</option>
          {client.spells.filter(s => isStaffSpell(client, s.id)).map(s => <option value={s.id}>{client.getEsfRecordById(s.id)?.name}</option>)}
        </select>
        <p class='text-xs opacity-75 mt-1'>Casting requires a staff, book or lens. Kiting uses your ranged weapon or selected spell and retreats from nearby enemies.</p>
      </label>}
      <label class='flex gap-2 items-center'><input type='checkbox' class='checkbox checkbox-sm' checked={potions} disabled={pending} onChange={e => setPotions(e.currentTarget.checked)} />Auto-potions from inventory</label>
      {potions && <div class='grid grid-cols-2 gap-2'>
        <label class='text-xs'>HP below %<input class='input w-full mt-1' type='number' value={hp} min={10} max={90} onChange={e => setHp(e.currentTarget.value)} /></label>
        <label class='text-xs'>Mana below %<input class='input w-full mt-1' type='number' value={tp} min={10} max={90} onChange={e => setTp(e.currentTarget.value)} /></label>
      </div>}
      <label class='flex gap-2 items-center'><input type='checkbox' class='checkbox checkbox-sm' checked={loot} disabled={pending} onChange={e => setLoot(e.currentTarget.checked)} />Pick up available drops (extra gold/minute)</label>
      <Input type='number' label='Minutes' value={minutes} min={1} max={120} onChange={setMinutes} />
      <div class={`rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} p-2 text-xs space-y-2`}>
        <p>Price increases every 50 character levels. Your level: {client.level}. Charged at start, then once per minute; stops if gold runs out.</p>
        <p role='status' aria-live='polite'>{status}</p>
      </div>
      <div class='grid grid-cols-3 gap-2'>
        <Button variant='primary' disabled={pending} onClick={start}>{pending ? 'Starting…' : 'Start'}</Button>
        <Button variant='secondary' onClick={() => send('#bot status')}>Status</Button>
        <Button variant='ghost' onClick={() => { stage.current = null; setPending(false); send('#bot stop'); }}>Stop</Button>
      </div>
    </div>
  </DialogBase>;
}
